import { useCanvasStore } from '../store/canvas-store';
import { useTimelineStore } from '../chrome/timeline/timeline-store';
import { usePanelStore } from '../chrome/panel/panel-store';
import type { Command } from '../store/types';

/**
 * First-run replay (review action #5): a pre-canned, zero-LLM-latency command
 * script that PERFORMS the product's superpowers in the user's canvas instead
 * of describing them — skeleton lands, sections fill incrementally, a layer
 * edit rewrites the headline, a motion timeline animates the hero and the
 * timeline dock opens playing. Every beat is one undo step; Esc fast-forwards
 * the remaining beats (the end state is always complete).
 */

export const REPLAY_FRAME_ID = 'viv-intro';
const GATE_KEY = 'codeframe.firstRunReplay';

export interface ReplayBeat {
  /** Transaction label — one beat = one undo step. */
  label: string;
  cmds: Command[];
  /** Pause AFTER the beat (skipped when fast-forwarding). */
  delayMs: number;
  /** Ephemeral layer highlight shown during the beat (cleared after). */
  highlight?: string;
}

const X = 120;
const Y = 100;
const W = 560;

const skeleton = `<div data-cf-id="root" class="p-8 bg-white text-slate-900" style="font-family:var(--font-mono,ui-monospace,monospace)">
  <header data-cf-id="hd" class="pb-4 mb-6 border-b border-dashed border-slate-300 text-slate-400">…</header>
  <section data-cf-id="hero" class="py-10 mb-6 border border-dashed border-slate-300 rounded-lg text-center text-slate-400">…</section>
  <section data-cf-id="feat" class="py-8 border border-dashed border-slate-300 rounded-lg text-center text-slate-400">…</section>
</div>`;

const headerHtml = `<nav class="flex items-center justify-between">
  <span class="font-bold tracking-tight">Vivarium</span>
  <span class="flex gap-4 text-sm text-slate-500"><span>Canvas</span><span>Components</span><span>Motion</span></span>
</nav>`;

const heroHtml = `<h1 data-cf-id="headline" class="text-3xl font-bold mb-3">Every frame is real</h1>
<p class="text-slate-500 mb-5">Live DOM on an infinite canvas — text selects, inputs type, motion moves.</p>
<button class="px-5 py-2 rounded-lg bg-indigo-600 text-white font-semibold">Try it</button>`;

const featHtml = `<div class="grid grid-cols-3 gap-4 px-6 text-left">
  <div class="p-4 rounded-lg bg-slate-50"><b>Components</b><p class="text-sm text-slate-500 mt-1">Promote any frame; instances live-update.</p></div>
  <div class="p-4 rounded-lg bg-slate-50"><b>Agent</b><p class="text-sm text-slate-500 mt-1">Viv drives the same command bus you do.</p></div>
  <div class="p-4 rounded-lg bg-slate-50"><b>Motion</b><p class="text-sm text-slate-500 mt-1">Keyframe timelines on real DOM nodes.</p></div>
</div>`;

export function firstRunBeats(): ReplayBeat[] {
  const fid = REPLAY_FRAME_ID;
  return [
    {
      label: 'viv: sketch the structure',
      delayMs: 1400,
      cmds: [
        { t: 'frame.create', id: fid, x: X, y: Y, w: W, title: 'Viv intro', html: skeleton },
        { t: 'viewport.center', bounds: { x: X, y: Y, w: W, h: 480 } },
        { t: 'select', ids: [fid] },
      ],
    },
    {
      label: 'viv: fill the header',
      delayMs: 1100,
      cmds: [{ t: 'frame.setNodeHtml', frameId: fid, cfNodeId: 'hd', html: headerHtml }],
    },
    {
      label: 'viv: fill the hero',
      delayMs: 1300,
      cmds: [{ t: 'frame.setNodeHtml', frameId: fid, cfNodeId: 'hero', html: heroHtml }],
    },
    {
      label: 'viv: fill the features',
      delayMs: 1300,
      cmds: [{ t: 'frame.setNodeHtml', frameId: fid, cfNodeId: 'feat', html: featHtml }],
    },
    {
      label: 'viv: rewrite the headline (layer edit)',
      delayMs: 1400,
      highlight: 'headline',
      cmds: [
        { t: 'frame.editNode', frameId: fid, cfNodeId: 'headline', text: 'Design at the speed of thought' },
      ],
    },
    {
      label: 'viv: animate the entrance',
      delayMs: 800,
      cmds: [
        { t: 'motion.init', frameId: fid },
        { t: 'motion.setDuration', frameId: fid, duration: 2000 },
        { t: 'motion.addTrack', frameId: fid, trackId: 'tr-hero', layerId: 'ly-hero', target: 'hero', label: 'hero' },
        { t: 'motion.setKeyframe', frameId: fid, trackId: 'tr-hero', layerId: 'ly-hero', prop: 'opacity', kf: { t: 0, value: 0, ease: 'easeOut' } },
        { t: 'motion.setKeyframe', frameId: fid, trackId: 'tr-hero', layerId: 'ly-hero', prop: 'opacity', kf: { t: 700, value: 1, ease: 'easeOut' } },
        { t: 'motion.setKeyframe', frameId: fid, trackId: 'tr-hero', layerId: 'ly-hero', prop: 'y', kf: { t: 0, value: 28, ease: 'easeOut' } },
        { t: 'motion.setKeyframe', frameId: fid, trackId: 'tr-hero', layerId: 'ly-hero', prop: 'y', kf: { t: 700, value: 0, ease: 'easeOut' } },
        { t: 'motion.addTrack', frameId: fid, trackId: 'tr-feat', layerId: 'ly-feat', target: 'feat', label: 'features' },
        { t: 'motion.setKeyframe', frameId: fid, trackId: 'tr-feat', layerId: 'ly-feat', prop: 'opacity', kf: { t: 400, value: 0, ease: 'easeOut' } },
        { t: 'motion.setKeyframe', frameId: fid, trackId: 'tr-feat', layerId: 'ly-feat', prop: 'opacity', kf: { t: 1200, value: 1, ease: 'easeOut' } },
        { t: 'motion.setPlayback', frameId: fid, playback: { autoplay: { mode: 'once' } } },
      ],
    },
  ];
}

export interface ReplayOpts {
  /** Injected for tests; defaults to real timeouts. */
  sleep?: (ms: number) => Promise<void>;
  /** false = ignore + don't write the run-once localStorage gate (menu re-run, tests). */
  gate?: boolean;
}

/** True when the replay has already run on this client. */
export function hasReplayed(): boolean {
  try { return localStorage.getItem(GATE_KEY) === '1'; } catch { return false; }
}

export async function runFirstRunReplay(opts: ReplayOpts = {}): Promise<void> {
  const gate = opts.gate ?? true;
  if (gate && hasReplayed()) return;
  if (gate) { try { localStorage.setItem(GATE_KEY, '1'); } catch { /* private mode */ } }

  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let fast = false;
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') fast = true; };
  const onClick = () => { fast = true; };
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onClick);
  }

  try {
    const store = useCanvasStore.getState();
    // Re-run: replace the previous intro frame rather than duplicating ids.
    if (store.doc.frames.some((f) => f.id === REPLAY_FRAME_ID)) {
      store.dispatch({ t: 'frame.delete', ids: [REPLAY_FRAME_ID] });
    }

    for (const beat of firstRunBeats()) {
      const s = useCanvasStore.getState();
      s.beginTransaction(beat.label);
      try {
        for (const c of beat.cmds) s.dispatch(c);
      } finally {
        s.endTransaction();
      }
      if (beat.highlight) {
        s.setLayerSelection({ frameId: REPLAY_FRAME_ID, cfNodeId: beat.highlight });
      }
      if (!fast) await sleep(beat.delayMs);
      if (beat.highlight) useCanvasStore.getState().setLayerSelection(null);
    }

    // The motion beat teaches the surface: open the dock over the playing
    // entrance, and land the user in the panel chat ready to type.
    useTimelineStore.getState().setOpen(true);
    usePanelStore.getState().setOpen(true);
  } finally {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onClick);
    }
  }
}
